import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from './../../context/auth-context.jsx';
import { adminApi } from './../../lib/admin-api/index.js';
import { normalizeFields, normalizeSteps, toRow } from './kyc-shared.js';
import { normalizeImpactsForSourceType, textToValues, valuesToText } from './kyc-step-utils.js';
import KycStepDependencyModal from './kyc-step-dependency-modal.jsx';

const MULTI_VALUE_SOURCE_TYPES = new Set(['select', 'country']);

function normalizeCountryOptions(payload) {
  if (Array.isArray(payload)) {
    return payload.map((item) => String(item?.value || item || '').trim()).filter(Boolean);
  }

  const candidates = [payload, payload?.items, payload?.data, payload?.countries];
  const result = [];
  candidates.forEach((candidate) => {
    if (Array.isArray(candidate)) {
      candidate.forEach((item) => {
        const value = String(item?.value || item?.code || item || '').trim();
        if (value) {
          result.push(value);
        }
      });
    }
  });
  return Array.from(new Set(result));
}

function normalizeDependencies(value) {
  return normalizeFields(value).map((item) => (item && typeof item === 'object' ? item : {}));
}

function KycFieldDependenciesPage({ basePath = '/system/user-settings/kyc' }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id: stepIdParam, fieldId: fieldIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const jurisdictionId = String(searchParams.get('jurisdictionId') || '').trim();
  const resolvedBasePath = String(basePath || '/system/user-settings/kyc').replace(/\/+$/, '') || '/system/user-settings/kyc';
  const jurisdictionQuery = jurisdictionId
    ? `?jurisdictionId=${encodeURIComponent(jurisdictionId)}`
    : '';
  const backPath = `${resolvedBasePath}/${encodeURIComponent(stepIdParam || '')}/edit${jurisdictionQuery}`;

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(null);
  const [targetField, setTargetField] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [modalError, setModalError] = useState('');
  const [modalState, setModalState] = useState({
    isOpen: false,
    dependencyId: '',
    fieldId: '',
    valuesText: '',
    selectValues: [],
    radioValue: '',
    checkboxState: 'checked',
  });
  const localeHint = typeof navigator !== 'undefined' && navigator.language
    ? navigator.language
    : 'en-US';

  const loadCountryDictionary = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const countriesResponse = await adminApi.getCountryDictionary(token);
      setCountryOptions(normalizeCountryOptions(countriesResponse));
    } catch (_requestError) {
      setCountryOptions([]);
    }
  }, [token]);

  const loadPageData = useCallback(async () => {
    if (!token || !jurisdictionId || !stepIdParam || !fieldIdParam) {
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const kycResponse = await adminApi.getKycSettings(token, jurisdictionId);

      const steps = normalizeSteps(kycResponse).map(toRow);
      const step = steps.find((item) => String(item.id) === String(stepIdParam));
      if (!step) {
        setStep(null);
        setTargetField(null);
        return;
      }

      const fields = normalizeFields(step.fields);
      const field = fields.find((item) => String(item?.uid || item?.id || item?.fieldId || '') === String(fieldIdParam)) || null;

      setStep(step);
      setTargetField(field);
    } catch (requestError) {
      setError(requestError.message || 'Failed to load dependencies');
      setStep(null);
      setTargetField(null);
    } finally {
      setIsLoading(false);
    }
  }, [token, jurisdictionId, stepIdParam, fieldIdParam]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  useEffect(() => {
    loadCountryDictionary();
  }, [loadCountryDictionary]);

  const candidateFields = useMemo(() => {
    if (!step || !targetField) {
      return [];
    }
    const targetId = String(targetField?.uid || targetField?.id || targetField?.fieldId || '');
    return normalizeFields(step.fields).filter((field) => String(field?.uid || field?.id || field?.fieldId || '') !== targetId).map((field) => ({
      fieldId: String(field?.uid || field?.id || field?.fieldId || ''),
      name: String(field?.name || ''),
      type: String(field?.type || 'text'),
      options: normalizeFields(field?.options).map((item) => {
        if (item && typeof item === 'object') {
          return String(item.value || item.label || '').trim();
        }
        return String(item ?? '').trim();
      }).filter(Boolean),
    }));
  }, [step, targetField]);

  const dependencies = useMemo(
    () => normalizeDependencies(targetField?.dependencies).map((item) => ({
      id: String(item?.uid || item?.id || '').trim(),
      fieldIdRelated: String(item?.fieldIdRelated || '').trim(),
      impacts: normalizeFields(item?.impacts).map((impact) => String(impact ?? '').trim()).filter(Boolean),
      checked: typeof item?.checked === 'boolean' ? item.checked : null,
    })),
    [targetField],
  );

  const sourceFieldById = useMemo(() => {
    const map = {};
    candidateFields.forEach((field) => {
      map[field.fieldId] = field;
    });
    return map;
  }, [candidateFields]);

  const sourceField = useMemo(
    () => sourceFieldById[modalState.fieldId] || null,
    [sourceFieldById, modalState.fieldId],
  );

  const sourceOptions = useMemo(() => {
    if (!sourceField) {
      return [];
    }
    if (sourceField.type === 'country') {
      return countryOptions;
    }
    return sourceField.options;
  }, [sourceField, countryOptions]);

  const openCreateModal = () => {
    setModalState({
      isOpen: true,
      dependencyId: '',
      fieldId: '',
      valuesText: '',
      selectValues: [],
      radioValue: '',
      checkboxState: 'checked',
    });
    setModalError('');
  };

  const openEditModal = (dependency) => {
    const impacts = normalizeFields(dependency?.impacts).map((item) => String(item ?? '').trim()).filter(Boolean);
    setModalState({
      isOpen: true,
      dependencyId: String(dependency?.id || ''),
      fieldId: String(dependency?.fieldIdRelated || ''),
      valuesText: valuesToText(impacts),
      selectValues: impacts,
      radioValue: String(impacts[0] || ''),
      checkboxState: dependency?.checked === false ? 'unchecked' : 'checked',
    });
    setModalError('');
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      dependencyId: '',
      fieldId: '',
      valuesText: '',
      selectValues: [],
      radioValue: '',
      checkboxState: 'checked',
    });
    setModalError('');
  };

  const saveDependency = async () => {
    if (!targetField) {
      return;
    }

    const sourceField = candidateFields.find((field) => field.fieldId === modalState.fieldId);
    if (!sourceField) {
      setModalError(t('kyc.dependencyFieldRequired'));
      return;
    }

    const payload = {
      fieldIdRelated: sourceField.fieldId,
      impacts: [],
      checked: null,
    };

    if (sourceField.type === 'checkbox') {
      payload.checked = modalState.checkboxState === 'checked';
    } else if (MULTI_VALUE_SOURCE_TYPES.has(sourceField.type)) {
      const impacts = normalizeImpactsForSourceType(modalState.selectValues, sourceField.type, localeHint);
      if (impacts.length === 0) {
        setModalError(t('kyc.selectAtLeastOneValue'));
        return;
      }
      payload.impacts = impacts;
    } else if (sourceField.type === 'radio') {
      const value = String(modalState.radioValue || '').trim();
      if (!value) {
        setModalError(t('kyc.selectValue2'));
        return;
      }
      payload.impacts = [value];
    } else {
      const impacts = normalizeImpactsForSourceType(textToValues(modalState.valuesText), sourceField.type, localeHint);
      if (impacts.length === 0) {
        setModalError(t('kyc.enterAtLeastOneValue'));
        return;
      }
      payload.impacts = impacts;
    }

    const targetFieldId = String(targetField?.uid || targetField?.id || targetField?.fieldId || '').trim();
    if (!targetFieldId) {
      setModalError(t('kyc.targetFieldMissing'));
      return;
    }

    setIsSaving(true);
    setModalError('');
    try {
      if (modalState.dependencyId) {
        await adminApi.updateKycDependency(token, jurisdictionId, stepIdParam, targetFieldId, modalState.dependencyId, payload);
      } else {
        await adminApi.createKycDependency(token, jurisdictionId, stepIdParam, targetFieldId, payload);
      }
      closeModal();
      await loadPageData();
    } catch (requestError) {
      setModalError(requestError.message || 'Failed to save dependency');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteDependency = async (dependencyId) => {
    if (!dependencyId || !targetField) {
      return;
    }

    const targetFieldId = String(targetField?.uid || targetField?.id || targetField?.fieldId || '').trim();
    setIsSaving(true);
    setError('');
    try {
      await adminApi.deleteKycDependency(token, jurisdictionId, stepIdParam, targetFieldId, dependencyId);
      await loadPageData();
    } catch (requestError) {
      setError(requestError.message || 'Failed to delete dependency');
    } finally {
      setIsSaving(false);
    }
  };

  if (!jurisdictionId || !stepIdParam || !fieldIdParam) {
    return <Navigate to={resolvedBasePath} replace />;
  }

  if (!isLoading && (!step || !targetField)) {
    return <Navigate to={backPath} replace />;
  }

  return (
    <div>
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('kyc.dependenciesTitle')}</h2>
          <p>{t('kyc.fieldColon')} {targetField?.name || t('kyc.unnamed')}</p>
        </div>
        <button type="button" className="btn btn-default" onClick={() => navigate(backPath)}>
          {t('kyc.backToStep')}
        </button>
      </div>

      <div className="widget-content searchable-container list">
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}

        <div className="card card-body">
          {isLoading ? (
            <div className="text-muted">{t('kyc.loading')}</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table search-table align-middle text-nowrap mb-0">
                  <thead className="header-item">
                    <tr>
                      <th>{t('kyc.colRelatedField')}</th>
                      <th>{t('kyc.colCondition')}</th>
                      <th style={{ width: '1%', whiteSpace: 'nowrap' }} className="text-end">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dependencies.length === 0 && (
                      <tr>
                        <td colSpan="3" className="text-center text-muted">{t('kyc.noDependencies')}</td>
                      </tr>
                    )}

                    {dependencies.map((dependency) => {
                      const source = sourceFieldById[dependency.fieldIdRelated];
                      const sourceName = source?.name || dependency.fieldIdRelated || t('kyc.unnamed');
                      const condition = source?.type === 'checkbox'
                        ? (dependency.checked === false ? t('kyc.checkboxUnchecked') : t('kyc.checkboxChecked'))
                        : dependency.impacts.join(', ');

                      return (
                        <tr key={dependency.id || `${dependency.fieldIdRelated}-${condition}`}>
                          <td>{sourceName}</td>
                          <td>{condition || '-'}</td>
                          <td className="text-end text-nowrap">
                            <button
                              type="button"
                              className="btn btn-sm btn-default me-1"
                              onClick={() => openEditModal(dependency)}
                              disabled={isSaving}
                              title={t('common.edit')}
                            >
                              <i className="fa fa-pen"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteDependency(dependency.id)}
                              disabled={isSaving || !dependency.id}
                              title={t('common.delete')}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-3">
                <button type="button" className="btn btn-primary" onClick={openCreateModal} disabled={isSaving}>
                  {t('kyc.addDependency')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <KycStepDependencyModal
        isOpen={modalState.isOpen}
        error={modalError}
        modalState={modalState}
        candidateFields={candidateFields}
        sourceField={sourceField}
        sourceOptions={sourceOptions}
        onClose={closeModal}
        onFieldChange={(fieldId) => setModalState((prev) => ({
          ...prev,
          fieldId,
          valuesText: '',
          selectValues: [],
          radioValue: '',
          checkboxState: 'checked',
        }))}
        onValuesTextChange={(valuesText) => setModalState((prev) => ({ ...prev, valuesText }))}
        onSelectValuesChange={(selectValues) => setModalState((prev) => ({ ...prev, selectValues }))}
        onRadioValueChange={(radioValue) => setModalState((prev) => ({ ...prev, radioValue }))}
        onCheckboxStateChange={(checkboxState) => setModalState((prev) => ({ ...prev, checkboxState }))}
        onClear={closeModal}
        onSave={saveDependency}
        showClear={false}
      />
    </div>
  );
}

export default KycFieldDependenciesPage;
